import React from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { useForm } from 'react-hook-form';
import type { Control, UseFormRegister, FormState, FieldValues, DefaultValues, SubmitHandler, UseFormWatch, UseFormSetValue, UseFormTrigger, UseFormGetValues, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEntityPermissions } from '../utils/permissions';
import { localizeFormErrors } from '../utils/localizeFormErrors';

interface FormDialogProps<T extends FieldValues> {
  value: T | null;
  onClose: () => void;
  onSave: (data: T) => void | T | Promise<void | T>;
  titleKey: string;
  width?: string;
  resolver?: Resolver<T>;
  entityName?: string; // Optional: Entity name for permission checking
  formAutoComplete?: string;
  children: (props: {
    control: Control<T>;
    register: UseFormRegister<T>;
    formState: FormState<T>;
    watch: UseFormWatch<T>;
    setValue: UseFormSetValue<T>;
    trigger: UseFormTrigger<T>;
    getValues: UseFormGetValues<T>;
  }) => React.ReactNode;
}

export function FormDialog<T extends FieldValues>({
  value,
  onClose,
  onSave,
  titleKey,
  width = "500px",
  resolver,
  entityName,
  formAutoComplete,
  children
}: FormDialogProps<T>) {
  const { t } = useTranslation();
  const { register, control, handleSubmit, formState, watch, setValue, trigger, getValues } = useForm<T>({
    defaultValues: value as DefaultValues<T> || undefined,
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const translateMessage = React.useCallback((message: string) => {
    const keyPrefixes = [
      'validation.',
      'form.',
      'forms.',
      'userManagement.',
      'gantt.',
      'resourceType.',
      'shiftModelTemplate.',
      'routing.',
      'material.',
      'csvImport.'
    ];

    const normalized = message.toLowerCase();
    if (
      normalized === 'required' ||
      normalized.includes('expected number, received null') ||
      normalized.includes('expected string, received null') ||
      normalized.includes('expected number, received nan') ||
      normalized.includes('expected string, received undefined') ||
      normalized.includes('expected number, received undefined') ||
      normalized.includes('expected date, received null') ||
      normalized.includes('expected date, received undefined')
    ) {
      return t('validation.required');
    }

    if (keyPrefixes.some(prefix => message.startsWith(prefix))) {
      return t(message);
    }

    return message;
  }, [t]);

  const localizeErrors = React.useCallback(
    (errors: any) => localizeFormErrors(errors, translateMessage),
    [translateMessage]
  );

  const localizedFormState = React.useMemo(() => {
    return {
      ...formState,
      errors: localizeErrors(formState.errors)
    };
  }, [formState, localizeErrors]);

  // Get permissions for this entity
  // Always call the hook (React rules), use placeholder when none provided
  const hasEntityName = !!entityName && entityName.trim().length > 0;
  const entityPermissions = useEntityPermissions(hasEntityName ? entityName.toLowerCase() : '__no_entity__');
  const isEditing = value && (value as any).id;
  
  // Determine if save button should be shown
  // If editing: need edit permission, if creating: need create permission
  const canSave = hasEntityName && entityPermissions 
    ? (isEditing ? entityPermissions.canEdit : entityPermissions.canCreate)
    : true; // Default to true if no entityName provided (backward compatibility)

  const onSubmit: SubmitHandler<T> = async (data: T) => {
    // Preserve the id field from the original value if it exists
    if (value && (value as any).id) {
      (data as any).id = (value as any).id;
    }

    await onSave(data);
  };

  const onError = (errors: any) => {
    const summarizedErrors = Object.entries(localizeErrors(errors) || {}).reduce((acc, [key, value]) => {
      const message = (value as any)?.message;
      acc[key] = message ? String(message) : 'Invalid';
      return acc;
    }, {} as Record<string, string>);
    console.error('FormDialog validation errors:', summarizedErrors);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event propagation
    onClose();
  };

  return (
    <DialogComponent
      width={width}
      isModal={true}
      visible={true}
      header={t(titleKey)}
      showCloseIcon={true}
      closeOnEscape={false}
      allowDragging={true}
      target="#root"
      animationSettings={{ effect: 'None' }}
      beforeClose={(e) => {
        e.cancel = true;
        onClose();
      }}
    >
      <form 
        onSubmit={handleSubmit(onSubmit, onError)} 
        className="p-4"
        onClick={(e) => e.stopPropagation()}
        autoComplete={formAutoComplete}
      >
        <div className="mb-4">
          {children({ control, register, formState: localizedFormState, watch, setValue, trigger, getValues })}
        </div>
        <div className="flex justify-end gap-2">
          <span data-testid="cancel-button">
            <ButtonComponent
              onClick={handleCancel}
              cssClass="e-outline"
              type="button"
            >
              {t('actions.cancel')}
            </ButtonComponent>
          </span>
          {canSave && (
            <span data-testid="save-button">
              <ButtonComponent
                type="submit"
                isPrimary={true}
                disabled={formState.isSubmitting}
              >
                {formState.isSubmitting ? t('actions.saving') : t('actions.save')}
              </ButtonComponent>
            </span>
          )}
        </div>
      </form>
    </DialogComponent>
  );
} 